<template>
  <div class="safedig-register-page" v-loading.fullscreen.lock="loading">
    <SafeDigHeader />
    <div class="safedig-register-main">
      <div class="safedig-register-content">
        <h1 class="safedig-register-title">{{ $t('register.createAccount') }}</h1>
        <p class="safedig-register-description">{{ $t('register.createAccountDescription') }}</p>
        <validation-observer ref="formValidator">
          <form @submit.prevent="attemptSubmit">
            <div v-if="validationErrors.length" class="safedig-validation-errors">
              <p class="safedig-validation-errors-title">{{ $t('register.fixErrorsBelow') || 'Please fix the following:' }}</p>
              <ul class="safedig-validation-errors-list">
                <li v-for="(msg, idx) in validationErrors" :key="idx">{{ msg }}</li>
              </ul>
            </div>
            <div class="safedig-register-fields">
              <div ref="fieldUserType" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldUserType') }">
                <label class="safedig-register-label">{{ $t('register.userType') }} <span class="safedig-required">*</span></label>
                <validation-provider :rules="{ required: true }" vid="User type" name="User type" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.userType" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.userType" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="key in userTypeOptionKeys" :key="key"
                        :label="$t('register.userTypeOptions.' + key)" :value="key" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldFirstName" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldFirstName') }">
                <label class="safedig-register-label">{{ $t('register.firstName') }}</label>
                <base-input v-model="form.firstName" vid="First name" name="First name" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldLastName" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldLastName') }">
                <label class="safedig-register-label">{{ $t('register.lastName') }}</label>
                <base-input v-model="form.lastName" vid="Last name" name="Last name" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldEmail" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldEmail') }">
                <label class="safedig-register-label">{{ $t('register.emailAddress') }}</label>
                <base-input v-model="form.email" vid="Email" name="Email" type="email" :rules="{ required: true, email: true }"
                  placeholder="" class="safedig-input-wrap" alternative @blur="onEmailBlur" />
              </div>
              <div ref="fieldUsername" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldUsername') }">
                <label class="safedig-register-label">{{ $t('register.username') }}</label>
                <base-input v-model="form.username" vid="Username" name="Username" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative autocomplete="off" />
                <p v-show="usernameAutoFillHintVisible" class="safedig-username-autofill-hint" role="status" aria-live="polite">
                  {{ $t('register.usernameFilledFromEmail') }}
                </p>
              </div>
              <div ref="fieldPassword" class="safedig-form-group safedig-password-wrap" :class="{ 'safedig-field-error': isFieldInvalid('fieldPassword') }">
                <label class="safedig-register-label">{{ $t('register.password') }}</label>
                <base-input v-model="form.password" vid="Password" name="Password" :type="passwordFieldType"
                  :rules="{ required: true, min: 6 }" placeholder="" class="safedig-input-wrap" alternative autocomplete="new-password" />
                <button type="button" class="safedig-password-toggle"
                  :aria-label="passwordFieldType === 'password' ? 'Show password' : 'Hide password'"
                  @click="togglePassword(1)">
                  <svg v-if="passwordFieldType === 'password'" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="safedig-eye-icon">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                    stroke="currentColor" class="safedig-eye-icon">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.228 3.228m3.228 3.228 3 3m-3.228-3.228-3.228 3.228" />
                  </svg>
                </button>
              </div>
              <div ref="fieldConfirmPassword" class="safedig-form-group safedig-password-wrap" :class="{ 'safedig-field-error': isFieldInvalid('fieldConfirmPassword') }">
                <label class="safedig-register-label">{{ $t('register.confirmPassword') }}</label>
                <base-input v-model="form.confirmPassword" vid="Confirm password" name="Confirm password" :type="confirmPasswordFieldType"
                  :rules="{ required: true, password_match: form.password }" placeholder="" class="safedig-input-wrap" alternative autocomplete="new-password" />
                <button type="button" class="safedig-password-toggle"
                  :aria-label="confirmPasswordFieldType === 'password' ? 'Show password' : 'Hide password'"
                  @click="togglePassword(2)">
                  <svg v-if="confirmPasswordFieldType === 'password'" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="safedig-eye-icon">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                    stroke="currentColor" class="safedig-eye-icon">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.228 3.228m3.228 3.228 3 3m-3.228-3.228-3.228 3.228" />
                  </svg>
                </button>
              </div>
              <div ref="fieldCompany" class="safedig-form-group">
                <label class="safedig-register-label">{{ $t('register.company') }}</label>
                <base-input v-model="form.company" vid="Company" name="Company" :rules="{}" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div class="safedig-form-group">
                <label class="safedig-register-label">{{ $t('register.country') }}</label>
                <el-select v-model="form.country" class="safedig-select full-width"
                  :placeholder="$t('register.selectOption')" popper-append-to-body>
                  <el-option label="United States of America" value="United States of America" />
                </el-select>
              </div>
              <div ref="fieldAddress" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldAddress') }">
                <label class="safedig-register-label">{{ $t('register.address') }}</label>
                <base-input v-model="form.address" vid="Address" name="Address" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldAddress2" class="safedig-form-group">
                <label class="safedig-register-label">{{ $t('register.address2') }}</label>
                <base-input v-model="form.address2" vid="Address 2" name="Address 2" :rules="{}" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldCity" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldCity') }">
                <label class="safedig-register-label">{{ $t('register.city') }}</label>
                <base-input v-model="form.city" vid="City" name="City" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldState" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldState') }">
                <label class="safedig-register-label">{{ $t('register.state') }}</label>
                <validation-provider :rules="{ required: true }" vid="State" name="State" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.state" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.state" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="s in usStates" :key="s" :label="s" :value="s" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldZipCode" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldZipCode') }">
                <label class="safedig-register-label">{{ $t('register.zipCode') }}</label>
                <base-input v-model="form.zipCode" vid="Zip code" name="Zip code" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative maxlength="5" inputmode="numeric" autocomplete="postal-code"
                  @input="acceptZipCode" />
              </div>
              <div ref="fieldPhone" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldPhone') }">
                <label class="safedig-register-label">{{ $t('register.phone') }}</label>
                <base-input v-model="form.phone" vid="Phone" name="Phone" :rules="{ required: true }" placeholder=""
                  class="safedig-input-wrap" alternative @input="acceptPhone" />
              </div>
              <div ref="fieldFax" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldFax') }">
                <label class="safedig-register-label">{{ $t('register.fax') }}</label>
                <base-input v-model="form.fax" vid="Fax" name="Fax" :rules="{}" placeholder=""
                  class="safedig-input-wrap" alternative />
              </div>
              <div ref="fieldEducation" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldEducation') }">
                <label class="safedig-register-label">{{ $t('register.education') }}</label>
                <validation-provider :rules="{ required: true }" vid="Education" name="Education" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.education" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.education" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="key in educationOptionKeys" :key="key"
                        :label="$t('register.educationOptions.' + key)" :value="key" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldExperience" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldExperience') }">
                <label class="safedig-register-label">{{ $t('register.experience') }}</label>
                <validation-provider :rules="{ required: true }" vid="Experience" name="Experience" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.experience" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.experience" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="key in experienceOptionKeys" :key="key"
                        :label="$t('register.experienceOptions.' + key)" :value="key" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldGender" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldGender') }">
                <label class="safedig-register-label">{{ $t('register.gender') }}</label>
                <validation-provider :rules="{ required: true }" vid="Gender" name="Gender" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.gender" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.gender" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="key in genderOptionKeys" :key="key"
                        :label="$t('register.genderOptions.' + key)" :value="key" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldBirthYear" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldBirthYear') }">
                <label class="safedig-register-label">{{ $t('register.birthYear') }}</label>
                <validation-provider :rules="{ required: true }" vid="Birth year" name="Birth year" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.birthYear" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.birthYear" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="y in birthYears" :key="y" :label="String(y)" :value="y" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
              <div ref="fieldHowDidYouHear" class="safedig-form-group" :class="{ 'safedig-field-error': isFieldInvalid('fieldHowDidYouHear') }">
                <label class="safedig-register-label">{{ $t('register.howDidYouHear') }}</label>
                <validation-provider :rules="{ required: true }" vid="How did you hear" name="How did you hear" v-slot="{ errors }">
                  <div class="safedig-field-body">
                    <input v-model="form.howDidYouHear" type="text" aria-hidden="true" tabindex="-1"
                      style="position:absolute;opacity:0;width:1px;height:1px;pointer-events:none" />
                    <el-select v-model="form.howDidYouHear" class="safedig-select full-width"
                      :class="{ 'is-invalid': errors[0] }"
                      :placeholder="$t('register.selectOption')" popper-append-to-body>
                      <el-option v-for="key in howDidYouHearOptionKeys" :key="key"
                        :label="$t('register.howDidYouHearOptions.' + key)" :value="key" />
                    </el-select>
                    <div v-if="errors[0]" class="invalid-feedback" style="display: block;">{{ errors[0] }}</div>
                  </div>
                </validation-provider>
              </div>
            </div>
            <div class="safedig-register-actions">
              <router-link to="/login" class="safedig-back-btn">{{ $t('register.goBack') }}</router-link>
              <base-button type="submit" native-type="submit" class="safedig-submit-btn" @click.native.prevent="attemptSubmit">{{ $t('register.submit') }}</base-button>
            </div>
          </form>
        </validation-observer>
      </div>
    </div>
    <div class="siteFooter footerOffWhite safedigFooter safedigLoginFooter">
      <div class="safedigCustomFooter">
        <div>
          <img src="../../../assets/safe-dig/safedig_logo_grayscale-01.png" class="safedigindiana811Logo"
            alt="Safe Dig Indiana" />
        </div>
        <p class="copyright">
          {{ $t('footer.copyright', { year }) }}<br>
          {{ $t('footer.allRightsReserved') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { Select, Option } from 'element-ui';
import SafeDigHeader from '@/components/safe-dig/Header.vue';

const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];

const VALIDATION_FIELD_REFS = ['fieldUserType', 'fieldFirstName', 'fieldLastName', 'fieldEmail', 'fieldUsername', 'fieldPassword', 'fieldConfirmPassword', 'fieldAddress', 'fieldCity', 'fieldState', 'fieldZipCode', 'fieldPhone', 'fieldFax', 'fieldEducation', 'fieldExperience', 'fieldGender', 'fieldBirthYear', 'fieldHowDidYouHear'];

export default {
  name: 'SafeDigRegister',
  components: {
    SafeDigHeader,
    [Select.name]: Select,
    [Option.name]: Option
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: false,
      validationAttempted: false,
      validationErrors: [],
      invalidFields: [],
      passwordFieldType: 'password',
      confirmPasswordFieldType: 'password',
      form: {
        userType: '',
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        company: '',
        country: 'United States of America',
        address: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        fax: '',
        education: '',
        experience: '',
        gender: '',
        birthYear: '',
        howDidYouHear: ''
      },
      usStates: US_STATES,
      userTypeOptionKeys: ['homeowner', 'professional', 'both'],
      educationOptionKeys: ['highSchool', 'someCollege', 'associateDegree', 'bachelorDegree', 'masterOrGreater', 'other'],
      experienceOptionKeys: ['lessThan1Year', 'oneToFiveYears', 'sixToTenYears', 'tenYearsOrMore'],
      howDidYouHearOptionKeys: ['radioAd', 'printAd', 'tvAd', 'directedByAgency', 'recommendedByFriend', 'work', 'other'],
      genderOptionKeys: ['male', 'female', 'other', 'preferNotToSay'],
      birthYears: Array.from({ length: 70 }, (_, i) => currentYear - 18 - i),
      year: new Date().getFullYear(),
      /** Email value last copied into username; avoids overwriting a customized username. */
      usernameAutoFillSource: '',
      usernameAutoFillHintVisible: false,
      usernameAutoFillHintTimer: null
    };
  },
  beforeDestroy() {
    if (this.usernameAutoFillHintTimer) {
      clearTimeout(this.usernameAutoFillHintTimer);
      this.usernameAutoFillHintTimer = null;
    }
  },
  methods: {
    togglePassword(which) {
      if (which === 1) this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
      else this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
    },
    acceptPhone() {
      const raw = (this.form.phone || '').replace(/\D/g, '');
      const m = raw.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      if (!m) { this.form.phone = ''; return; }
      this.form.phone = !m[2] ? m[1] : `(${m[1]}) ${m[2]}${m[3] ? '-' + m[3] : ''}`;
    },
    acceptZipCode() {
      const digits = (this.form.zipCode || '').replace(/\D/g, '').slice(0, 5);
      this.form.zipCode = digits;
    },
    isValidEmailFormat(value) {
      const v = (value || '').trim();
      return v.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },
    onEmailBlur() {
      const email = (this.form.email || '').trim();
      if (!this.isValidEmailFormat(email)) return;
      const u = (this.form.username || '').trim();
      const src = (this.usernameAutoFillSource || '').trim();
      const maySyncUsername = u === '' || u === src;
      if (!maySyncUsername) return;
      if (u === email && src === email) return;
      const filled = u !== email;
      this.form.username = email;
      this.usernameAutoFillSource = email;
      if (filled) {
        this.usernameAutoFillHintVisible = true;
        if (this.usernameAutoFillHintTimer) clearTimeout(this.usernameAutoFillHintTimer);
        this.usernameAutoFillHintTimer = setTimeout(() => {
          this.usernameAutoFillHintVisible = false;
          this.usernameAutoFillHintTimer = null;
        }, 6000);
      }
    },
    runManualValidation() {
      const errs = [];
      const invalidRefs = [];
      const f = this.form;
      const refOrder = VALIDATION_FIELD_REFS;
      let firstInvalidRef = null;
      const add = (ref) => { invalidRefs.push(ref); if (!firstInvalidRef) firstInvalidRef = ref; };
      if (!(f.userType || '').trim()) { errs.push(`${this.$t('register.userType')}: This field is required.`); add(refOrder[0]); }
      if (!(f.firstName || '').trim()) { errs.push(`${this.$t('register.firstName')}: This field is required.`); add(refOrder[1]); }
      if (!(f.lastName || '').trim()) { errs.push(`${this.$t('register.lastName')}: This field is required.`); add(refOrder[2]); }
      if (!(f.email || '').trim()) { errs.push(`${this.$t('register.emailAddress')}: This field is required.`); add(refOrder[3]); }
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) { errs.push(`${this.$t('register.emailAddress')}: Please enter a valid email.`); add(refOrder[3]); }
      if (!(f.username || '').trim()) { errs.push(`${this.$t('register.username')}: This field is required.`); add(refOrder[4]); }
      if (!(f.password || '')) { errs.push(`${this.$t('register.password')}: This field is required.`); add(refOrder[5]); }
      else if (f.password.length < 6) { errs.push(`${this.$t('register.password')}: Must be at least 6 characters.`); add(refOrder[5]); }
      if (!(f.confirmPassword || '')) { errs.push(`${this.$t('register.confirmPassword')}: This field is required.`); add(refOrder[6]); }
      else if (f.password !== f.confirmPassword) { errs.push(`${this.$t('register.confirmPassword')}: Password and Confirm Password must match.`); add(refOrder[6]); }
      if (!(f.address || '').trim()) { errs.push(`${this.$t('register.address')}: This field is required.`); add(refOrder[7]); }
      if (!(f.city || '').trim()) { errs.push(`${this.$t('register.city')}: This field is required.`); add(refOrder[8]); }
      if (!(f.state || '').trim()) { errs.push(`${this.$t('register.state')}: This field is required.`); add(refOrder[9]); }
      if (!(f.zipCode || '').trim()) { errs.push(`${this.$t('register.zipCode')}: This field is required.`); add(refOrder[10]); }
      else if (!/^\d{5}$/.test((f.zipCode || '').trim())) { errs.push(`${this.$t('register.zipCode')}: Please enter a valid 5-digit ZIP code.`); add(refOrder[10]); }
      if (!(f.phone || '').trim()) { errs.push(`${this.$t('register.phone')}: This field is required.`); add(refOrder[11]); }
      else if ((f.phone || '').replace(/\D/g, '').length < 10) { errs.push(`${this.$t('register.phone')}: Please enter a valid 10-digit phone number.`); add(refOrder[11]); }
      if (!(f.education || '').trim()) { errs.push(`${this.$t('register.education')}: This field is required.`); add(refOrder[13]); }
      if (!(f.experience || '').trim()) { errs.push(`${this.$t('register.experience')}: This field is required.`); add(refOrder[14]); }
      if (!(f.gender || '').trim()) { errs.push(`${this.$t('register.gender')}: This field is required.`); add(refOrder[15]); }
      if (!(f.birthYear || '').toString().trim()) { errs.push(`${this.$t('register.birthYear')}: This field is required.`); add(refOrder[16]); }
      if (!(f.howDidYouHear || '').trim()) { errs.push(`${this.$t('register.howDidYouHear')}: This field is required.`); add(refOrder[17]); }
      return { errors: errs, firstFieldRef: firstInvalidRef, invalidFieldRefs: invalidRefs };
    },
    isFieldInvalid(refName) {
      return this.invalidFields.includes(refName);
    },
    attemptSubmit() {
      this.validationAttempted = true;
      this.validationErrors = [];
      this.invalidFields = [];
      const { errors: manualErrs, firstFieldRef, invalidFieldRefs } = this.runManualValidation();
      if (manualErrs.length > 0) {
        this.validationErrors = manualErrs;
        this.invalidFields = invalidFieldRefs;
        if (this.$refs.formValidator) this.$refs.formValidator.validate();
        this.$nextTick(() => {
          const refName = firstFieldRef || VALIDATION_FIELD_REFS[0];
          const el = this.$refs[refName];
          if (el && (el.$el || el).scrollIntoView) {
            (el.$el || el).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      this.invalidFields = [];
      this.onSubmit();
    },
    getFieldLabel(fieldName) {
      const map = {
        'First name': this.$t('register.firstName'),
        'Last name': this.$t('register.lastName'),
        'Email': this.$t('register.emailAddress'),
        'Username': this.$t('register.username'),
        'Password': this.$t('register.password'),
        'Confirm password': this.$t('register.confirmPassword'),
        'Company': this.$t('register.company'),
        'Address': this.$t('register.address'),
        'City': this.$t('register.city'),
        'State': this.$t('register.state'),
        'Zip code': this.$t('register.zipCode'),
        'Phone': this.$t('register.phone')
      };
      return map[fieldName] || fieldName;
    },
    onSubmit() {
      this.loading = true;
      const payload = {
        user_type: 'individual',
        safedig_user_type: this.form.userType,
        employee_first_name: this.form.firstName,
        employee_last_name: this.form.lastName,
        employee_email: this.form.email,
        employee_username: this.form.username,
        employee_phone_num: this.form.phone,
        employee_address: [this.form.address, this.form.address2].filter(Boolean).join(', '),
        employee_city: this.form.city,
        employee_state: this.form.state,
        employee_zipcode: this.form.zipCode,
        password: this.form.password,
        address: this.form.address,
        address2: this.form.address2,
        company: this.form.company,
        country: this.form.country,
        fax: this.form.fax,
        education: this.form.education,
        experience: this.form.experience,
        gender: this.form.gender,
        birth_year: this.form.birthYear ? Number(this.form.birthYear) : null,
        how_did_you_hear: this.form.howDidYouHear,
        courses: [],
        i_agree: true
      };
      delete this.$http.defaults.headers['authorization'];
      this.$http
        .post('employees/register', payload)
        .then(() => {
          Swal.fire({
            title: this.$t('register.createAccount'),
            text: 'Your account has been created. You can now log in.',
            icon: 'success'
          }).then(() => {
            this.$router.push('/login');
          });
        })
        .catch((err) => {
          const msg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Registration failed. Please try again.';
          Swal.fire({ title: 'Error', text: msg, icon: 'error' });
        })
        .finally(() => { this.loading = false; });
    }
  }
};
</script>

<style scoped>
.safedig-register-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.safedig-register-main {
  flex: 1;
  padding: 2rem 1rem 3rem;
}

.safedig-register-content {
  max-width: 560px;
  margin: 0 auto;
  background: #fff;
  border-radius: 4px;
  border-top: 4px solid #94bcd5;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, .12), 0 2px 4px 0 rgba(0, 0, 0, .08);
  padding: 12px;
}

.safedig-register-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e3a5f;
  margin: 0 0 0.5rem;
}

.safedig-register-description {
  color: #555;
  font-size: 1rem;
  margin: 0 0 1.5rem;
}

.safedig-validation-errors {
  background: #fdf2f2;
  border: 1px solid #e3342f;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.safedig-validation-errors-title {
  font-weight: 600;
  color: #9c3c21;
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
}

.safedig-validation-errors-list {
  margin: 0;
  padding-left: 1.25rem;
  color: #555;
  font-size: 0.875rem;
  line-height: 1.5;
}

.safedig-register-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.safedig-form-group {
  position: relative;
}

/* Consistent height for control + error: same for base-input and select fields */
.safedig-form-group >>> .form-group .input-group-alternative,
.safedig-form-group >>> .form-group .input-group {
  min-height: 44px;
}

.safedig-form-group >>> .invalid-feedback {
  min-height: 1.25rem;
  line-height: 1.25;
  font-size: 0.875rem;
  margin-top: 2px;
}

.safedig-field-body {
  min-height: calc(44px + 1.25rem + 2px);
}

.safedig-register-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: #9c3c21;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.safedig-required {
  color: #e3342f;
  margin-left: 2px;
}

.safedig-username-autofill-hint {
  margin: 6px 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #3d5a73;
  animation: safedig-hint-in 0.35s ease-out;
}

@keyframes safedig-hint-in {
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.safedig-input-wrap >>> .input-group-alternative {
  box-shadow: none;
}
.safedig-input-wrap>>>.form-control {
  height: 44px;
  border: 1px solid #ccc;
  border-radius: 4px !important;
  font-size: 1rem;
  background: #fff;
}

.safedig-input-wrap>>>.form-control.is-invalid {
  border-color: #e3342f;
}

.safedig-password-wrap .safedig-password-toggle {
  position: absolute;
  right: 10px;
  top: 30px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
}

.safedig-password-wrap .safedig-eye-icon {
  width: 20px;
  height: 20px;
}

.safedig-select.full-width {
  width: 100%;
}

.safedig-select>>>.el-input__inner {
  height: 44px;
  border: 1px solid #ccc;
  border-radius: 4px !important;
}

.safedig-select.is-invalid>>>.el-input__inner {
  border-color: #e3342f;
}

/* Highlight fields with validation errors */
.safedig-field-error >>> .form-control,
.safedig-field-error >>> .input-group-alternative .form-control {
  border-color: #e3342f !important;
}
.safedig-field-error >>> .el-input__inner {
  border-color: #e3342f !important;
}

.safedig-register-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;
}

.safedig-back-btn {
  display: inline-block;
  background: #555 !important;
  color: #fff !important;
  border: none;
  padding: 10px 28px;
  font-weight: 600;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
}

.safedig-back-btn:hover {
  background: #333 !important;
  color: #fff !important;
}

.safedig-submit-btn {
  background: #9c3c21 !important;
  color: #fff !important;
  border: none !important;
  padding: 10px 28px;
  font-weight: 600;
  border-radius: 4px !important;
}

.safedig-submit-btn:hover {
  background: #85341c !important;
  color: #fff !important;
}

.siteFooter {
  width: 100%;
}

.safedigFooter {
  background: #e8e8e8;
}

.safedigLoginFooter .safedigCustomFooter {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
}

.safedigindiana811Logo {
  height: 60px;
  width: auto;
}

.copyright {
  margin: 0;
  color: #6b6b6b;
  font-size: 0.85rem;
  line-height: 1.5;
}
</style>
